import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import WizardLayout from './WizardLayout';
import BatchDetailsAccordion from './BatchDetailsAccordion';
import Shipment from './Shipment';
import { initialShipmentState } from './initialState';

const initialShipmentValidationState = { comment: '', volume: '' };

const ShipperAllocation = ({
  batchState,
  setBatchState,
  onPrev,
  onNext,
  onClose,
  validationState,
  setValidationState,
}) => {
  const { shipments } = batchState;

  // Given to shipment forms for updating state of a specific shipment.
  // setShipmentState :: i -- index of shipment to modify
  //                  -> f -- function to transform shipment
  //                  -> ()
  const setShipmentState = R.curry((i, f) => {
    setBatchState(s => R.assoc('shipments', R.adjust(i, f, s.shipments), s));
  });

  // Given to shipment forms for updating validation of a specific shipment.
  // setShipmentValidationState :: i -- index of validation to modify
  //                            -> f -- function to transform validation
  //                            -> ()
  const setShipmentValidationState = R.curry((i, f) => {
    setValidationState(R.adjust(i, f));
  });

  const handleAddShipment = () => {
    setValidationState(R.append(initialShipmentValidationState));
    setBatchState(s => R.assoc('shipments', R.append(initialShipmentState, s.shipments), s));
  };

  const handleRemoveShipment = index => {
    setValidationState(R.remove(index, 1));
    setBatchState(R.assoc('shipments', R.remove(index, 1, shipments)));
  };

  const isInvalid = [
    R.map(R.compose(R.isNil, R.prop('shipper')), shipments),
    R.map(R.compose(R.isNil, R.prop('bookingOrigin')), shipments),
    R.map(
      R.pipe(R.propOr(0, 'volume'), val => !val || val === '0' || val < 100),
      shipments
    ),
    R.map(R.compose(R.not, R.isEmpty, R.prop('comment')), validationState),
  ]
    .flat()
    .includes(true);

  const buttons = (
    <>
      <Button onClick={onPrev} sx={{ width: 104 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={onNext} sx={{ width: 104 }}>
        Review
      </Button>
    </>
  );

  return (
    <WizardLayout buttons={buttons} onClose={onClose} sx={{ backgroundColor: '#FAFAFA' }}>
      <Stack spacing={2}>
        <BatchDetailsAccordion batchState={batchState} />
        <Card elevation={1} sx={{ pt: 2, px: 2 }}>
          <CardHeader
            slotProps={{
              subheader: { variant: 'text' },
              title: { sx: { color: 'primary.dark' }, variant: 'h5' },
            }}
            subheader="Please provide the following details for your shipment(s)."
            sx={{ pl: 1 }}
            title="Shipper Allocation"
          />
          <CardContent sx={{ px: 1 }}>
            <Stack spacing={3}>
              {shipments.map((shipment, index) => (
                <Fragment key={shipment.id || `shipment-${index}`}>
                  {shipments.length > 1 && (
                    <Box
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                      sx={{ mb: 0, pb: 0 }}
                    >
                      <Typography sx={{ color: 'text.primary', lineHeight: 1 }} variant="subtitle1">
                        Shipment {index + 1}
                      </Typography>
                      <Button
                        onClick={() => handleRemoveShipment(index)}
                        startIcon={<ClearIcon sx={{ mt: '-3px' }} />}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.dark',
                          },
                          color: 'error.main',
                        }}
                        variant="text"
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                  <Shipment
                    batchState={batchState}
                    index={index}
                    setShipmentState={setShipmentState(index)}
                    setValidationState={setShipmentValidationState(index)}
                    shipment={shipment}
                    validationState={validationState[index]}
                  />
                </Fragment>
              ))}
            </Stack>
          </CardContent>
          <CardActions sx={{ ml: 1, mr: 2, my: 1, p: 0 }}>
            <Button
              onClick={handleAddShipment}
              startIcon={<AddIcon sx={{ mt: '-3px' }} />}
              sx={{
                '&:hover': {
                  backgroundColor: 'secondary.light',
                  color: 'secondary.dark',
                },
                color: 'secondary.main',
                mb: 2,
                ml: 0,
              }}
              variant="text"
            >
              Add Shipment
            </Button>
          </CardActions>
        </Card>
      </Stack>
    </WizardLayout>
  );
};

ShipperAllocation.propTypes = {
  batchState: PropTypes.shape({
    shipments: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  setBatchState: PropTypes.func.isRequired,
  setValidationState: PropTypes.func.isRequired,
  validationState: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default ShipperAllocation;
